import {
  calculateQuickscanResult,
  getQuickscanQuestions,
  type CompanyType,
  type QuickscanQuestion,
} from '@wtta/scoring'
import { useEffect, useReducer, useRef } from 'react'

import { CompanyTypeStep } from './components/CompanyTypeStep'
import { IntroStep } from './components/IntroStep'
import { LeadCaptureStep } from './components/LeadCaptureStep'
import { QuestionStep } from './components/QuestionStep'
import { ScorePreview } from './components/ScorePreview'
import { loadConfig, type WidgetConfig } from './config'
import { startAutoResize } from './lib/auto-resize'
import { buildPardotFormUrl, buildSuccessRedirectUrl } from './lib/pardot'
import { createUnsignedToken } from './lib/token'
import { initialState, reducer } from './state'

export interface QuickscanWidgetProps {
  config?: Partial<WidgetConfig>
  widgetId?: string
}

export function QuickscanWidget({ config: overrides, widgetId = 'wtta-quickscan' }: QuickscanWidgetProps) {
  const config = loadConfig(overrides)
  const [state, dispatch] = useReducer(reducer, initialState)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!containerRef.current) return undefined
    return startAutoResize({
      widgetId,
      target: containerRef.current,
    })
  }, [widgetId])

  return (
    <div ref={containerRef} className="wtta-widget" data-widget-id={widgetId}>
      {renderStep(state, dispatch, config)}
    </div>
  )
}

function renderStep(
  state: ReturnType<typeof reducer>,
  dispatch: React.Dispatch<Parameters<typeof reducer>[1]>,
  config: WidgetConfig,
) {
  if (state.step.kind === 'intro') {
    return <IntroStep onStart={() => dispatch({ type: 'start' })} />
  }

  if (state.step.kind === 'company-type') {
    return (
      <CompanyTypeStep
        onSelect={(companyType: CompanyType) =>
          dispatch({ type: 'select-company-type', companyType })
        }
      />
    )
  }

  if (state.step.kind === 'questions') {
    return renderQuestionStep(state, dispatch, config)
  }

  if (state.step.kind === 'score-preview') {
    return (
      <ScorePreview
        result={state.step.result}
        onContinue={() => transitionToLeadCapture(state, dispatch, config)}
      />
    )
  }

  if (state.step.kind === 'lead-capture') {
    return (
      <LeadCaptureStep
        pardotUrl={state.step.pardotUrl}
        config={config}
        onRestart={() => dispatch({ type: 'reset' })}
      />
    )
  }

  return null
}

function renderQuestionStep(
  state: ReturnType<typeof reducer>,
  dispatch: React.Dispatch<Parameters<typeof reducer>[1]>,
  config: WidgetConfig,
) {
  if (state.step.kind !== 'questions') return null

  const { companyType, questionIndex } = state.step
  const questions = getQuickscanQuestions(companyType)
  const question = questions[questionIndex]

  if (!question) {
    return null
  }

  return (
    <QuestionStep
      question={question}
      selectedValue={state.answers[question.id]}
      questionNumber={questionIndex + 1}
      totalQuestions={questions.length}
      onAnswer={(value) =>
        handleAnswer({
          dispatch,
          companyType,
          questionIndex,
          questions,
          question,
          value,
          config,
          existingAnswers: state.answers,
        })
      }
      onBack={questionIndex > 0 ? () => dispatch({ type: 'go-to-question', index: questionIndex - 1 }) : null}
    />
  )
}

interface HandleAnswerInput {
  dispatch: React.Dispatch<Parameters<typeof reducer>[1]>
  companyType: CompanyType
  questionIndex: number
  questions: QuickscanQuestion[]
  question: QuickscanQuestion
  value: string
  config: WidgetConfig
  existingAnswers: Record<string, string>
}

function handleAnswer(input: HandleAnswerInput): void {
  const { dispatch, questionIndex, questions, question, value, companyType, existingAnswers, config } = input

  dispatch({ type: 'answer-question', questionId: question.id, value })

  const isLastQuestion = questionIndex === questions.length - 1
  if (!isLastQuestion) {
    dispatch({ type: 'go-to-question', index: questionIndex + 1 })
    return
  }

  const finalAnswers = { ...existingAnswers, [question.id]: value }
  const result = calculateQuickscanResult(companyType, finalAnswers)
  dispatch({ type: 'show-score', result })

  void config
}

function transitionToLeadCapture(
  state: ReturnType<typeof reducer>,
  dispatch: React.Dispatch<Parameters<typeof reducer>[1]>,
  config: WidgetConfig,
): void {
  if (state.step.kind !== 'score-preview') return
  if (!state.companyType) return

  const token = createUnsignedToken(state.companyType, state.answers)
  const successRedirectUrl = buildSuccessRedirectUrl(config.reportPageUrl, token)
  const pardotUrl = buildPardotFormUrl({
    config,
    companyType: state.companyType,
    answers: state.answers,
    result: state.step.result,
    token,
    successRedirectUrl,
  })

  dispatch({
    type: 'show-lead-capture',
    result: state.step.result,
    pardotUrl,
    token,
  })
}
